import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useAxios } from "@/composables/api.js";
import { catchError } from "@/composables/error.js";
import { useAppStore } from "@/stores/app.js";

export const useCompanyStore = defineStore("company", () => {
  const appStore = useAppStore();

  const companies = ref([]);

  const currentCompany = computed(() => {
    if (!appStore.currentRoute.params.companyId) return null;

    return companies.value.find(
      (company) => company.company_id === appStore.currentRoute.params.companyId
    );
  });

  async function createCompany(company) {
    try {
      const { data } = await useAxios.post("/companies", company);

      companies.value.push({ ...data.company, role: 0 });

      return data.company;
    } catch (error) {
      return catchError(error);
    }
  }

  async function editCompany(company_id, company) {
    try {
      const { data } = await useAxios.put(`/companies/${company_id}`, company);

      const companyIndex = getCompanyIndex(company_id);

      Object.assign(companies.value[companyIndex], data.company);

      return data.company;
    } catch (error) {
      return catchError(error);
    }
  }

  async function getCompany(company_id) {
    try {
      const { data } = await useAxios.get(`/companies/${company_id}`);

      const companyIndex = getCompanyIndex(company_id);

      Object.assign(companies.value[companyIndex], data.company);

      await getCompanyCredits(data.company.company_id);

      return data.company;
    } catch (error) {
      return catchError(error);
    }
  }

  async function getCompanyCredits(company_id) {
    try {
      const { data } = await useAxios.get(`/companies/${company_id}/credits`);

      const companyIndex = getCompanyIndex(company_id);

      companies.value[companyIndex].credits = data;

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function buyCompanyCredits(company_id, nbCredits) {
    try {
      const { data } = await useAxios.post(`/companies/${company_id}/credits`, {
        credits: nbCredits,
      });

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function getCompanyTransaction(company_id, transaction_id) {
    try {
      const { data } = await useAxios.get(
        `/companies/${company_id}/transactions/${transaction_id}`
      );

      return data.transaction;
    } catch (error) {
      return catchError(error);
    }
  }

  async function getCompanyMembers(company_id) {
    try {
      const { data } = await useAxios.get(`/companies/${company_id}/members`);

      const companyIndex = getCompanyIndex(company_id);

      companies.value[companyIndex].members = data.members;

      return data.members;
    } catch (error) {
      return catchError(error);
    }
  }

  async function updateCompanyMemberRole(company_id, member_id, role) {
    try {
      const { data } = await useAxios.put(
        `/companies/${company_id}/members/${member_id}`,
        { role }
      );

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function revokeCompanyMembers(company_id, member_id) {
    try {
      const { data } = await useAxios.delete(
        `/companies/${company_id}/members/${member_id}`
      );

      const memberIndex = currentCompany.value.members.findIndex(
        (memberObj) => memberObj.member_id === member_id
      );

      const companyIndex = getCompanyIndex(company_id);

      companies.value[companyIndex].members.splice(memberIndex, 1);

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function addCompanyMember(company_id, member) {
    try {
      const { data } = await useAxios.post(
        `/companies/${company_id}/members`,
        member
      );

      const companyIndex = getCompanyIndex(company_id);

      companies.value[companyIndex].members.push(data.member);

      return data.member;
    } catch (error) {
      return catchError(error);
    }
  }

  async function getMemberInvitation(invitation_id) {
    try {
      const { data } = await useAxios.get(
        `/companies-members/${invitation_id}`
      );

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function getCompanyTransactions(company_id) {
    try {
      const { data } = await useAxios.get(
        `/companies/${company_id}/transactions`
      );

      const companyIndex = getCompanyIndex(company_id);

      companies.value[companyIndex].transactions = data.transactions;

      return data.transactions;
    } catch (error) {
      return catchError(error);
    }
  }

  async function getCompanyTransactionInvoice(company_id, transaction_id) {
    try {
      const { data } = await useAxios.get(
        `/companies/${company_id}/transactions/${transaction_id}/invoice`
      );

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function getCompanyStats(company_id, stat) {
    try {
      const statObj = {};

      const { data } = await useAxios.get(
        `/companies/${company_id}/stats/${stat}`
      );

      statObj[stat] = { ...data };

      return statObj;
    } catch (error) {
      return catchError(error);
    }
  }

  function getCompanyIndex(company_id) {
    return companies.value.findIndex(
      (company) => company.company_id === company_id
    );
  }

  function clear() {
    companies.value = [];
  }

  return {
    currentCompany,
    companies,
    clear,
    getCompanyIndex,
    createCompany,
    editCompany,
    getCompany,
    getCompanyCredits,
    buyCompanyCredits,
    getCompanyTransaction,
    getCompanyMembers,
    addCompanyMember,
    updateCompanyMemberRole,
    revokeCompanyMembers,
    getMemberInvitation,
    getCompanyTransactions,
    getCompanyTransactionInvoice,
    getCompanyStats,
  };
});
